import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard,
  FileText,
  Calendar,
  Layers,
  Link2,
  BarChart3,
  Users,
  UserPlus,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Command,
  ListOrdered,
  Rss,
  ImageIcon,
  FolderOpen,
  CheckSquare,
  CalendarClock,
  CreditCard,
  MessageSquare,
  Zap,
  ShoppingBag,
  Bot,
  HelpCircle,
  LayoutGrid,
  Pin,
  Search,
  Star,
  Wand2,
  Sparkles,
  LayoutTemplate,
} from "lucide-react";
import { useAuthStore } from "../../store/authStore";
import { aiUsageAPI, teamsAPI } from "../../services/api";
import { useNavFavorites } from "../../hooks/useNavFavorites";
import NotificationBell from "./NotificationBell";
import LocaleSwitcher from "./LocaleSwitcher";
import WorkspaceSwitcher from "./WorkspaceSwitcher";
import PlugDLogo from "./PlugDLogo";
import { CommandPalette, type CommandItem } from "./CommandPalette";

interface NavItem {
  path: string;
  label: string;
  icon: React.ElementType;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

interface AppLayoutProps {
  children: React.ReactNode;
}

export default function AppLayout({ children }: AppLayoutProps) {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const mainRef = useRef<HTMLElement>(null);
  const favorites = useNavFavorites(user?.id ?? null);

  // Global Cmd+K / Ctrl+K opens the command palette. Registered here
  // so it works from every route without per-page wiring. Esc-close
  // is handled inside the palette itself.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Wrapped so the useMemos below that depend on NAV_SECTIONS don't
  // re-run on every render (they previously saw a fresh array reference
  // every time, which defeated their memoization).
  const NAV_SECTIONS = useMemo<NavSection[]>(() => [
    {
      title: t("nav_section_connect", "Connect"),
      items: [{ path: "/accounts", label: t("nav_accounts", "Social Accounts"), icon: Users }],
    },
    {
      title: "",
      items: [
        { path: "/dashboard", label: t("nav_dashboard", "Dashboard"), icon: LayoutDashboard },
      ],
    },
    {
      title: t("nav_section_ai", "AI Studio"),
      items: [
        { path: "/agent", label: t("nav_ai_agent", "AI Agent"), icon: Bot },
      ],
    },
    {
      title: t("nav_section_publish", "Publish"),
      items: [
        { path: "/workspace", label: t("nav_pub_studio", "Content Studio"), icon: LayoutGrid },
        { path: "/builder", label: t("nav_pub_builder", "Page Builder"), icon: Layers },
        { path: "/posts", label: t("nav_pub_posts", "Posts"), icon: FileText },
        { path: "/calendar", label: t("nav_pub_calendar", "Calendar"), icon: Calendar },
        { path: "/queue", label: t("nav_pub_queue", "Queue"), icon: ListOrdered },
        { path: "/templates", label: t("nav_pub_templates", "Templates"), icon: LayoutTemplate },
        { path: "/bulk", label: t("nav_pub_bulk", "Bulk Schedule"), icon: CalendarClock },
        { path: "/approvals", label: t("nav_pub_approvals", "Approvals"), icon: CheckSquare },
      ],
    },
    {
      title: t("nav_section_engage", "Engage"),
      items: [
        { path: "/inbox", label: t("nav_engage_inbox", "Inbox"), icon: MessageSquare },
        { path: "/automations", label: t("nav_engage_automations", "Automations"), icon: Zap },
      ],
    },
    {
      title: t("nav_section_tools", "Tools"),
      items: [
        { path: "/links", label: t("nav_tools_links", "Short Links"), icon: Link2 },
        { path: "/editor", label: t("nav_tools_editor", "Image Editor"), icon: ImageIcon },
        { path: "/media", label: t("nav_tools_media", "Media Library"), icon: FolderOpen },
        { path: "/rss", label: t("nav_tools_rss", "RSS Feeds"), icon: Rss },
        { path: "/marketplace", label: t("nav_tools_marketplace", "Marketplace"), icon: ShoppingBag },
      ],
    },
    {
      title: t("nav_section_insights", "Insights"),
      items: [
        { path: "/analytics", label: t("nav_insights_analytics", "Analytics"), icon: BarChart3 },
      ],
    },
    {
      title: t("nav_section_settings", "Settings"),
      items: [
        { path: "/team", label: t("nav_settings_team", "Team"), icon: UserPlus },
        { path: "/settings", label: t("nav_settings_settings", "Settings"), icon: Settings },
        { path: "/settings/white-label", label: t("nav_settings_white_label", "White Label"), icon: Wand2 },
        { path: "/billing", label: t("nav_settings_billing", "Billing"), icon: CreditCard },
        { path: "/help", label: t("nav_settings_help", "Help Center"), icon: HelpCircle },
      ],
    },
  ], [t]);

  // Get current page title
  const getPageTitle = () => {
    const currentPath = location.pathname;
    for (const section of NAV_SECTIONS) {
      const item = section.items.find(i => i.path === currentPath);
      if (item) return item.label;
    }
    // Handle specific routes
    if (currentPath.startsWith("/compose")) {
      // /compose         → New Post
      // /compose/:id     → Edit Post  (id is a 24-char MongoDB ObjectId)
      const match = currentPath.match(/^\/compose\/([^/?#]+)/);
      if (match && match[1]) {
        return t("nav_title_compose_edit", "Edit Post");
      }
      return t("nav_title_compose", "New Post");
    }
    return t("nav_title_dashboard", "Dashboard");
  };

  // Fetch AI usage for the sidebar meter
  const { data: aiUsage } = useQuery({
    queryKey: ["ai-usage-sidebar"],
    queryFn: () => aiUsageAPI.getSummary({ days: 30 }).then((r) => r.data),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  // Fetch white label settings
  const { data: whitelabelData } = useQuery({
    queryKey: ["whitelabel-settings", user?.team_id],
    queryFn: async () => {
      if (!user?.team_id) return null;
      const response = await teamsAPI.getWhiteLabel(user.team_id);
      return response.data;
    },
    enabled: !!user?.team_id && !!localStorage.getItem("access_token"),
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: false,
    refetchOnWindowFocus: false,
  });

  const whitelabelSettings = whitelabelData?.white_label;

  // Apply white label branding
  useEffect(() => {
    if (whitelabelSettings) {
      // Set document title
      if (whitelabelSettings.brand_name) {
        document.title = whitelabelSettings.brand_name;
      }

      // Set favicon
      if (whitelabelSettings.favicon_url) {
        const link = document.querySelector("link[rel='icon']") as HTMLLinkElement || document.createElement('link');
        link.rel = 'icon';
        link.href = whitelabelSettings.favicon_url;
        if (!document.querySelector("link[rel='icon']")) {
          document.head.appendChild(link);
        }
      }

      // Apply custom CSS
      if (whitelabelSettings.custom_css) {
        let styleTag = document.getElementById('white-label-styles') as HTMLStyleElement;
        if (!styleTag) {
          styleTag = document.createElement('style');
          styleTag.id = 'white-label-styles';
          document.head.appendChild(styleTag);
        }
        styleTag.textContent = whitelabelSettings.custom_css;
      }

      // Apply primary color as CSS variable
      const root = document.documentElement;
      if (whitelabelSettings.primary_color) {
        root.style.setProperty('--color-primary', whitelabelSettings.primary_color);
      }
      if (whitelabelSettings.secondary_color) {
        root.style.setProperty('--color-secondary', whitelabelSettings.secondary_color);
      }
    }
  }, [whitelabelSettings]);

  const planUsed = aiUsage?.plan_used ?? 0;
  const planLimit = aiUsage?.plan_limit ?? 0;
  const isUnlimited = planLimit === -1;
  const usagePct = planLimit > 0 ? Math.min((planUsed / planLimit) * 100, 100) : 0;
  const totalCost = aiUsage?.total_cost_usd ?? 0;

  // Paths inside the "AI Studio" section — used to switch the active
  // accent from blue to the shared green token so AI surfaces read as distinct. Derived
  // once per NAV_SECTIONS change instead of string-matching section.title
  // on every render of every row.
  const aiSectionTitle = t("nav_section_ai", "AI Studio");
  const aiPaths = useMemo(() => {
    const section = NAV_SECTIONS.find((s) => s.title === aiSectionTitle);
    return new Set<string>((section?.items || []).map((i) => i.path));
  }, [NAV_SECTIONS, aiSectionTitle]);

  // Flatten all nav items with their section label for the pinned
  // derivation + command palette index.
  const allNavItems = useMemo(() => {
    const flat: Array<{
      path: string;
      label: string;
      icon: React.ElementType;
      section: string;
    }> = [];
    for (const section of NAV_SECTIONS) {
      for (const item of section.items) {
        flat.push({ ...item, section: section.title || "" });
      }
    }
    return flat;
  }, [NAV_SECTIONS]);

  // Pinned nav items in display order — preserve the order the user
  // starred them (favorites.pinned) and skip paths no longer in the
  // menu (route deprecated since the pin was saved).
  const pinnedItems = useMemo(() => {
    const byPath = new Map(allNavItems.map((i) => [i.path, i]));
    return favorites.pinned
      .map((p) => byPath.get(p))
      .filter((i): i is (typeof allNavItems)[number] => Boolean(i));
  }, [favorites.pinned, allNavItems]);

  // Command palette index: every nav item + a handful of quick actions.
  const commandItems = useMemo<CommandItem[]>(() => {
    const nav: CommandItem[] = allNavItems.map((i) => ({
      id: `nav:${i.path}`,
      label: i.label,
      section: i.section,
      path: i.path,
      icon: i.icon,
      aiAccent: aiPaths.has(i.path),
    }));
    const quick: CommandItem[] = [
      {
        id: "action:new-post",
        label: t("cmdk_action_new_post", "New post"),
        section: t("nav_section_publish", "Publish"),
        path: "/compose",
        icon: FileText,
      },
      {
        id: "action:logout",
        label: t("cmdk_action_logout", "Log out"),
        section: t("nav_section_settings", "Settings"),
        icon: LogOut,
        action: logout,
      },
    ];
    return [...quick, ...nav];
  }, [allNavItems, aiPaths, t, logout]);

  // Scroll main content to top on route change
  useEffect(() => {
    mainRef.current?.scrollTo(0, 0);
  }, [location.pathname]);

  // Full-viewport routes manage their own internal scrolling —
  // the outer <main> must NOT add a second scrollbar.
  const isFullViewportRoute =
    location.pathname.startsWith('/builder') ||
    location.pathname.startsWith('/studio') ||
    location.pathname.startsWith('/agent');

  return (
    <div className="flex h-screen bg-n-page">
      {/* ── Sidebar ───────────────────────────────────────────────── */}
      <aside
        className={`${sidebarOpen ? "w-64" : "w-[72px]"} relative bg-white border-r border-n-border flex flex-col transition-all duration-300 ease-in-out`}
      >
        {/* Logo block — flat white card treatment, matching the app cards */}
        <div
          className="relative overflow-hidden border-b border-n-border cursor-pointer group bg-white"
          onClick={() => navigate("/")}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { navigate("/"); } }}
        >
          <div className="relative px-4 py-4 flex items-center gap-3">
            <PlugDLogo size={36} className="flex-shrink-0 rounded-[10px] ring-1 ring-n-border" />
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <h1 className="font-semibold text-[16px] tracking-tight text-n-text">PlugD</h1>
                <p className="text-[10.5px] font-medium uppercase tracking-[0.18em] text-n-green">
                  {t("nav_tagline", "Publish anywhere")}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Workspace Switcher — ringed card */}
        {sidebarOpen && (
          <div className="px-3 pt-3">
            <div className="n-card-flat">
              <WorkspaceSwitcher />
            </div>
          </div>
        )}

        {/* Command-palette launcher — only when expanded */}
        {sidebarOpen && (
          <div className="px-3 pt-3">
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-n-sm bg-white border border-n-border hover:bg-n-hover transition-colors text-left group"
              aria-label={t("nav_button_palette", "Open command palette")}
            >
              <Search className="w-3.5 h-3.5 text-n-text-lt group-hover:text-n-text transition-colors" />
              <span className="flex-1 text-[12.5px] text-n-text-md group-hover:text-n-text">
                {t("nav_palette_hint", "Jump to…")}
              </span>
              <kbd className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-n-xs bg-n-page border border-n-border text-[10px] font-medium text-n-text-md">
                <Command className="w-2.5 h-2.5" />K
              </kbd>
            </button>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 px-3 py-3 overflow-y-auto scrollbar-hide">
          {/* Pinned section — rendered only when user has stars. */}
          {pinnedItems.length > 0 && (
            <div className="mb-4">
              {sidebarOpen ? (
                <div className="px-2 mb-1.5 flex items-center gap-1.5">
                  <Pin className="w-3 h-3 text-amber-600" />
                  <span className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-n-text-md">
                    {t("nav_section_pinned", "Pinned")}
                  </span>
                </div>
              ) : (
                <div className="mx-3 mb-2 h-px bg-n-border" />
              )}
              <div className="space-y-0.5">
                {pinnedItems.map((item) => (
                  <NavRow
                    key={`pinned-${item.path}`}
                    item={item}
                    sidebarOpen={sidebarOpen}
                    location={location}
                    aiAccent={aiPaths.has(item.path)}
                    isPinned
                    onTogglePin={() => favorites.toggle(item.path)}
                  />
                ))}
              </div>
            </div>
          )}

          {NAV_SECTIONS.map((section, sIdx) => {
            const isAISection = section.title === aiSectionTitle;
            return (
              <div key={sIdx} className={sIdx > 0 || pinnedItems.length > 0 ? "mt-4" : ""}>
                {section.title && sidebarOpen && (
                  <div className="px-2 mb-1.5 flex items-center justify-between">
                    <span className={`text-[10.5px] font-semibold uppercase tracking-[0.16em] ${isAISection ? "text-n-green" : "text-n-text-md"}`}>
                      {section.title}
                    </span>
                    {isAISection && aiUsage && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-n-xs bg-n-green-bg text-[10px] font-medium text-n-green border border-n-green/20"
                        title={t("nav_ai_cost", "AI cost this month")}
                      >
                        <Sparkles className="w-2.5 h-2.5" />
                        ${totalCost.toFixed(2)}
                      </span>
                    )}
                  </div>
                )}
                {!sidebarOpen && sIdx > 0 && (
                  <div className="mx-3 mb-2 h-px bg-n-border" />
                )}
                <div className="space-y-0.5">
                  {section.items.map((item) => (
                    <NavRow
                      key={item.path}
                      item={item}
                      sidebarOpen={sidebarOpen}
                      location={location}
                      aiAccent={aiPaths.has(item.path)}
                      isPinned={favorites.isPinned(item.path)}
                      onTogglePin={() => favorites.toggle(item.path)}
                    />
                  ))}
                </div>

                {/* AI Credits — flat white card, same surface as the rest of the sidebar */}
                {isAISection && sidebarOpen && aiUsage && (
                  <Link
                    to="/ai-usage"
                    className="mx-1 mt-2 block rounded-n bg-white border border-n-border p-3 hover:bg-n-hover transition-colors group"
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-n-green">
                        <Sparkles className="w-2.5 h-2.5" />
                        {t("nav_ai_credits", "AI Credits")}
                      </span>
                      <span className="text-[10.5px] font-bold text-n-text">
                        {isUnlimited ? (
                          t("nav_ai_unlimited", "Unlimited")
                        ) : (
                          <>{planUsed}<span className="text-n-text-lt">/{planLimit}</span></>
                        )}
                      </span>
                    </div>
                    {!isUnlimited && (
                      <div className="h-1.5 rounded-full bg-n-green-bg overflow-hidden">
                        <div
                          className="h-full rounded-full bg-n-green transition-[width] duration-500"
                          style={{ width: `${usagePct}%` }}
                        />
                      </div>
                    )}
                    <p className="text-[10px] text-n-green mt-1.5 font-medium">
                      {t("nav_ai_view_usage", "View usage details →")}
                    </p>
                  </Link>
                )}
              </div>
            );
          })}
        </nav>

        {/* User Section — white card surface */}
        <div className="px-3 py-3">
          <div className="relative rounded-n bg-white border border-n-border p-2.5 hover:bg-n-hover transition-colors">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-9 h-9 rounded-full bg-n-green flex items-center justify-center flex-shrink-0 font-semibold text-white text-[13.5px]">
                {user?.name?.charAt(0)?.toUpperCase() || "U"}
              </div>
              {sidebarOpen && (
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold tracking-tight text-n-text truncate">
                    {user?.name || "User"}
                  </p>
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-n-blue-bg text-[10px] font-semibold text-n-blue ring-1 ring-n-blue/20 mt-0.5">
                    {user?.plan?.charAt(0)?.toUpperCase()}{user?.plan?.slice(1)?.toLowerCase() || "Free"}
                  </span>
                </div>
              )}
              <button
                onClick={logout}
                className="flex-shrink-0 p-1.5 rounded-lg text-n-text-lt hover:text-n-text hover:bg-n-hover transition-colors"
                title={t("nav_button_logout", "Logout")}
                aria-label={t("nav_button_logout", "Logout")}
              >
                <LogOut size={15} />
              </button>
            </div>
          </div>
        </div>

        {/* Floating collapse handle — attached to the sidebar's right edge.
            Replaces the old bottom-row button so the nav reads as a single
            clean surface instead of having a footer toolbar. */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="absolute top-1/2 -right-3 -translate-y-1/2 w-6 h-6 rounded-full bg-white border border-n-border text-n-text-md hover:text-n-green shadow-n-sm flex items-center justify-center transition-colors z-10"
          title={sidebarOpen ? t("nav_button_collapse", "Collapse sidebar") : t("nav_button_expand", "Expand sidebar")}
          aria-label={sidebarOpen ? t("nav_button_collapse", "Collapse sidebar") : t("nav_button_expand", "Expand sidebar")}
        >
          {sidebarOpen ? <ChevronLeft size={13} /> : <ChevronRight size={13} />}
        </button>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar — solid white */}
        <div className="n-topbar relative z-10">
          {/* Left: Page Title */}
          <div className="flex items-center min-w-0">
            <h2 className="n-page-title">{getPageTitle()}</h2>
          </div>

          {/* Center: Search — triggers the command palette.
              Keeps the visual slot the old dead ``<input>`` occupied
              but makes it a real action: click or Cmd+K to open.
              The input was never wired to anything before this pass. */}
          <div className="flex-1 max-w-sm">
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              className="w-full flex items-center gap-2 px-3 py-1.5 rounded-n-sm bg-white border border-n-border hover:bg-n-hover transition-colors text-left group"
              aria-label={t("nav_button_palette", "Open command palette")}
            >
              <Search size={14} className="text-n-text-lt group-hover:text-n-text transition-colors flex-shrink-0" />
              <span className="flex-1 text-[12.5px] text-n-text-md group-hover:text-n-text truncate">
                {t("nav_search_placeholder_cmd", "Search or jump to…")}
              </span>
              <kbd className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-n-xs bg-n-page border border-n-border text-[10px] font-medium text-n-text-md flex-shrink-0">
                <Command className="w-2.5 h-2.5" />K
              </kbd>
            </button>
          </div>

          {/* Right: Locale Switcher + Notification Bell */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <LocaleSwitcher />
            <NotificationBell />
          </div>
        </div>

        <main key={location.pathname} ref={mainRef} className={`flex-1 min-h-0 bg-n-page animate-page-fade-in ${isFullViewportRoute ? 'overflow-hidden flex flex-col' : 'overflow-y-auto'}`}>
          {children}
        </main>
      </div>

      {/* Global command palette — mounted once at the layout root so
          Cmd+K works from every route. */}
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        items={commandItems}
        onNavigate={(path) => navigate(path)}
      />
    </div>
  );
}

// ─── NavRow ──────────────────────────────────────────────────────────
//
// Polished single-row renderer used by both the Pinned section and
// the regular NAV_SECTIONS. Handles three concerns the old inline
// version didn't:
//   1. Shared green accent for AI paths, blue for everything else.
//   2. Star / unstar on hover — keeps chrome quiet until interaction.
//   3. Collapsed-rail tooltip — the label floats on hover so the user
//      doesn't lose their bearings in the 72-px rail.

interface NavRowProps {
  item: { path: string; label: string; icon: React.ElementType };
  sidebarOpen: boolean;
  location: { pathname: string };
  aiAccent: boolean;
  isPinned: boolean;
  onTogglePin: () => void;
}

const NavRow: React.FC<NavRowProps> = ({
  item,
  sidebarOpen,
  location,
  aiAccent,
  isPinned,
  onTogglePin,
}) => {
  const { t } = useTranslation();
  const exactMatchOnly = ["/settings", "/dashboard", "/ai"];
  const isActive =
    location.pathname === item.path ||
    (!exactMatchOnly.includes(item.path) &&
      location.pathname.startsWith(item.path + "/"));
  const IconComponent = item.icon;

  const activeClasses = aiAccent
    ? "bg-n-green-bg text-n-green border border-n-green/20"
    : "bg-n-blue-bg text-n-blue border border-n-blue/20";

  const iconColor = isActive
    ? aiAccent
      ? "text-n-green"
      : "text-n-blue"
    : "text-n-text-md group-hover:text-n-text";

  return (
    <div className="relative group">
      <Link
        to={item.path}
        className={`flex items-center gap-3 px-2.5 py-[6px] rounded-n-sm border text-[13px] font-medium tracking-tight transition-colors ${
          isActive
            ? activeClasses
            : "text-n-text-md border-transparent hover:bg-n-hover hover:text-n-text"
        } ${sidebarOpen ? "" : "justify-center"}`}
      >
        <IconComponent size={17} className={`flex-shrink-0 transition-colors ${iconColor}`} />
        {sidebarOpen && (
          <>
            <span className="flex-1 truncate">{item.label}</span>
            {/* Star shows on hover when unpinned; stays visible when
                pinned so the user can see the state at a glance. */}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onTogglePin();
              }}
              className={`flex-shrink-0 p-0.5 rounded-md transition-all ${
                isPinned
                  ? "text-amber-500 opacity-100"
                  : "text-n-text-lt opacity-0 group-hover:opacity-100 hover:text-amber-500"
              }`}
              aria-label={
                isPinned
                  ? t("nav_row_unpin", "Unpin from top")
                  : t("nav_row_pin", "Pin to top")
              }
              title={
                isPinned
                  ? t("nav_row_unpin", "Unpin from top")
                  : t("nav_row_pin", "Pin to top")
              }
            >
              <Star
                size={12}
                className={isPinned ? "fill-amber-400" : ""}
              />
            </button>
          </>
        )}
      </Link>

      {/* Collapsed-rail tooltip — floats to the right on hover so the
          user doesn't lose track of what each icon is. Only renders
          when the sidebar is collapsed. */}
      {!sidebarOpen && (
        <span
          role="tooltip"
          className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2 py-1 rounded-lg bg-n-text text-white text-[11.5px] font-medium tracking-tight whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity shadow-[0_4px_12px_-4px_rgba(0,0,0,0.3)] z-20"
        >
          {item.label}
        </span>
      )}
    </div>
  );
};
